import { ArrowRight } from "lucide-react";

// ---------- helpers -------------------------------------------------------
const color = (v) => (v ? `var(${v})` : "var(--accent)");
const mix = (v, pct) =>
  v ? `color-mix(in srgb, var(${v}) ${pct}%, transparent)` : undefined;

// ---------- sub-diagrams --------------------------------------------------
function Cards({ items }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 16,
        margin: "16px 0",
      }}
    >
      {items.map((item, i) => (
        <article
          key={i}
          className="card"
          style={{
            padding: "16px 16px 16px 20px",
            borderLeft: `3px solid ${color(item.colorVar)}`,
            background: mix(item.colorVar, 6),
          }}
        >
          <h4
            style={{
              margin: 0,
              fontFamily: "var(--font-body)",
              fontSize: 16,
              fontWeight: 600,
              color: color(item.colorVar),
            }}
          >
            {item.label}
          </h4>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--muted)",
              margin: "4px 0 8px",
            }}
          >
            {item.sub}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.6,
              color: "var(--text-2)",
            }}
          >
            {item.desc}
          </p>
        </article>
      ))}
    </div>
  );
}

function Flow({ items }) {
  return (
    <ol
      style={{
        listStyle: "none",
        padding: 0,
        margin: "16px 0",
        display: "grid",
        gap: 8,
      }}
    >
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
            padding: "8px 8px",
            borderRadius: 12,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontSize: 24,
              width: 40,
              height: 40,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--surface-strong)",
              borderRadius: 12,
              flexShrink: 0,
            }}
          >
            {item.icon}
          </span>
          <div style={{ flex: 1, paddingTop: 2 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "var(--text)",
              }}
            >
              {item.label}
            </div>
            <div style={{ fontSize: 15, color: "var(--text-2)", lineHeight: 1.5 }}>
              {item.note}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function Balance({ left, right }) {
  const Side = ({ side, tone }) => (
    <div
      className="card"
      style={{
        padding: 20,
        textAlign: "center",
        background: mix(tone, 6),
        borderColor: mix(tone, 24),
      }}
    >
      <div aria-hidden="true" style={{ fontSize: 32 }}>
        {side.icon}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: `var(${tone})`,
          margin: "8px 0",
        }}
      >
        {side.label}
      </div>
      <div style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>
        {side.desc}
      </div>
    </div>
  );
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        gap: 16,
        alignItems: "center",
        margin: "16px 0",
      }}
    >
      <Side side={left} tone="--danger" />
      <div aria-hidden="true" style={{ fontSize: 24, color: "var(--muted)" }}>
        ⚖️
      </div>
      <Side side={right} tone="--info" />
    </div>
  );
}

function SymptomGrid({ items }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 16,
        margin: "16px 0",
      }}
    >
      {items.map((group, i) => (
        <section key={i} className="card" style={{ padding: 16 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--accent)",
              marginBottom: 8,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
            }}
          >
            {group.system}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {group.symptoms.map((s, j) => (
              <li
                key={j}
                style={{
                  fontSize: 15,
                  color: "var(--text-2)",
                  marginBottom: 4,
                  paddingLeft: 8,
                  borderLeft: "2px solid color-mix(in srgb, var(--accent) 35%, transparent)",
                  lineHeight: 1.45,
                }}
              >
                {s}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Pathway({ steps }) {
  return (
    <ol
      style={{
        listStyle: "none",
        padding: 0,
        margin: "16px 0",
        display: "grid",
        gap: 8,
      }}
    >
      {steps.map((step, i) => (
        <li
          key={i}
          style={{ display: "flex", gap: 16, alignItems: "flex-start" }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: color(step.colorVar),
              color: "var(--bg)",
              fontWeight: 700,
              fontSize: 14,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            {step.num}
          </span>
          <div
            className="card"
            style={{
              flex: 1,
              padding: "12px 16px",
              borderLeft: `3px solid ${color(step.colorVar)}`,
              background: mix(step.colorVar, 6),
            }}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: color(step.colorVar),
                marginBottom: 4,
              }}
            >
              {step.label}
            </div>
            <div style={{ fontSize: 15, color: "var(--text-2)", lineHeight: 1.55 }}>
              {step.body}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function DefinitionBox({ term, definition, key_enzyme, key_bacteria }) {
  return (
    <aside
      className="card"
      style={{
        padding: "20px 22px",
        margin: "16px 0",
        background: "color-mix(in srgb, var(--mod-2) 7%, var(--surface))",
        borderColor: "color-mix(in srgb, var(--mod-2) 30%, var(--border))",
      }}
    >
      <div
        className="t-heading"
        style={{ color: "var(--mod-2)", marginBottom: 8 }}
      >
        {term}
      </div>
      <p
        style={{
          fontSize: 15,
          color: "var(--text-2)",
          lineHeight: 1.6,
          margin: "0 0 12px",
        }}
      >
        {definition}
      </p>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--muted)",
          marginBottom: 8,
        }}
      >
        Key enzyme:{" "}
        <span style={{ color: "var(--mod-2)", fontWeight: 600 }}>
          {key_enzyme}
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {key_bacteria.map((b, i) => (
          <span
            key={i}
            style={{
              background: "color-mix(in srgb, var(--mod-2) 12%, transparent)",
              border: "1px solid color-mix(in srgb, var(--mod-2) 30%, transparent)",
              color: "var(--mod-2)",
              fontSize: 12,
              padding: "4px 10px",
              borderRadius: 999,
              fontFamily: "var(--font-mono)",
            }}
          >
            {b}
          </span>
        ))}
      </div>
    </aside>
  );
}

function EnzymeDiagram({ steps }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "center",
        justifyContent: "center",
        margin: "16px 0",
      }}
    >
      {steps.map((step, i) => (
        <div
          key={i}
          style={{ display: "flex", alignItems: "center", gap: 12 }}
        >
          <div
            className="card"
            style={{
              padding: 16,
              textAlign: "center",
              width: 160,
            }}
          >
            <div aria-hidden="true" style={{ fontSize: 28, marginBottom: 8 }}>
              {step.icon}
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--text)",
                marginBottom: 4,
              }}
            >
              {step.label}
            </div>
            <div
              style={{
                fontSize: 13,
                color: "var(--muted)",
                lineHeight: 1.45,
              }}
            >
              {step.note}
            </div>
          </div>
          {i < steps.length - 1 && (
            <ArrowRight
              size={18}
              color="var(--muted)"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          )}
        </div>
      ))}
    </div>
  );
}

function TwoColumn({ left, right }) {
  const Col = (col) => (
    <section
      className="card"
      style={{
        padding: 16,
        background: mix(col.colorVar, 8),
        borderColor: mix(col.colorVar, 28),
      }}
    >
      <header
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          fontWeight: 700,
          color: color(col.colorVar),
          marginBottom: 10,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        {col.title}
      </header>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {col.points.map((p, j) => (
          <li
            key={j}
            style={{
              fontSize: 15,
              color: "var(--text-2)",
              marginBottom: 6,
              paddingLeft: 10,
              borderLeft: `2px solid ${mix(col.colorVar, 55)}`,
              lineHeight: 1.45,
            }}
          >
            {p}
          </li>
        ))}
      </ul>
    </section>
  );
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 16,
        margin: "16px 0",
      }}
    >
      <Col {...left} />
      <Col {...right} />
    </div>
  );
}

function CauseWeb({ items }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 12,
        margin: "16px 0",
      }}
    >
      {items.map((item, i) => (
        <div
          key={i}
          className="card"
          style={{
            padding: 14,
            background: "color-mix(in srgb, var(--mod-3) 6%, transparent)",
            borderColor: "color-mix(in srgb, var(--mod-3) 22%, var(--border))",
          }}
        >
          <div aria-hidden="true" style={{ fontSize: 24, marginBottom: 8 }}>
            {item.icon}
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "var(--mod-3)",
              marginBottom: 4,
            }}
          >
            {item.label}
          </div>
          <div
            style={{
              fontSize: 14,
              color: "var(--text-2)",
              lineHeight: 1.5,
            }}
          >
            {item.note}
          </div>
        </div>
      ))}
    </div>
  );
}

function FoodGrid({ categories }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 16,
        margin: "16px 0",
      }}
    >
      {categories.map((cat, i) => (
        <section
          key={i}
          className="card"
          style={{
            padding: 16,
            background: mix(cat.colorVar, 6),
            borderColor: mix(cat.colorVar, 25),
          }}
        >
          <header
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: color(cat.colorVar),
              marginBottom: 10,
            }}
          >
            {cat.title}
          </header>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 10,
            }}
          >
            {cat.foods.map((f, j) => (
              <span
                key={j}
                style={{
                  background: mix(cat.colorVar, 12),
                  color: color(cat.colorVar),
                  fontSize: 13,
                  padding: "3px 10px",
                  borderRadius: 999,
                  border: `1px solid ${mix(cat.colorVar, 28)}`,
                  fontFamily: "var(--font-mono)",
                }}
              >
                {f}
              </span>
            ))}
          </div>
          <p
            style={{
              fontSize: 14,
              color: "var(--text-2)",
              fontStyle: "italic",
              lineHeight: 1.55,
              margin: 0,
            }}
          >
            {cat.note}
          </p>
        </section>
      ))}
    </div>
  );
}

function ProbioticTable({ items }) {
  return (
    <div style={{ margin: "16px 0", display: "grid", gap: 8 }}>
      {items.map((item, i) => (
        <div
          key={i}
          className="card"
          style={{
            padding: 16,
            background: "color-mix(in srgb, var(--mod-4) 6%, var(--surface))",
            borderColor: "color-mix(in srgb, var(--mod-4) 22%, var(--border))",
          }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "var(--mod-4)",
              fontStyle: "italic",
              marginBottom: 4,
            }}
          >
            {item.bacteria}
          </div>
          <div
            style={{
              fontSize: 15,
              color: "var(--text-2)",
              lineHeight: 1.5,
              marginBottom: 6,
            }}
          >
            {item.role}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--muted)",
              fontFamily: "var(--font-mono)",
            }}
          >
            Sources: {item.source}
          </div>
        </div>
      ))}
    </div>
  );
}

function SupplementList({ items }) {
  return (
    <div style={{ margin: "16px 0", display: "grid", gap: 8 }}>
      {items.map((item, i) => (
        <div
          key={i}
          className="card"
          style={{
            padding: 16,
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "var(--text)",
              }}
            >
              {item.name}
            </div>
            <div
              style={{
                fontSize: 14,
                color: "var(--text-2)",
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              {item.action}
            </div>
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--mod-3)",
              fontFamily: "var(--font-mono)",
              textAlign: "right",
              maxWidth: 120,
              lineHeight: 1.4,
            }}
          >
            {item.timing}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Illustration (real image asset) -------------------------------
function Illustration({ src, alt, caption, credit }) {
  return (
    <figure
      data-testid="lesson-illustration"
      style={{
        margin: "20px 0 24px",
        padding: 0,
        background: "var(--surface)",
        borderRadius: 16,
        border: "1px solid var(--border)",
        overflow: "hidden",
        boxShadow:
          "0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(0,0,0,0.12)",
      }}
    >
      <img
        src={src}
        alt={alt || caption || "course illustration"}
        loading="lazy"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          background: "#FAF7F1",
        }}
      />
      {(caption || credit) && (
        <figcaption
          style={{
            padding: "10px 16px 14px",
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--muted)",
            fontFamily: "var(--font-body)",
            borderTop: "1px solid var(--border)",
          }}
        >
          {caption}
          {credit && (
            <span
              style={{
                display: "block",
                marginTop: 4,
                fontSize: 11,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                opacity: 0.7,
              }}
            >
              {credit}
            </span>
          )}
        </figcaption>
      )}
    </figure>
  );
}

// ---------- main dispatcher ----------------------------------------------
export default function DiagramRenderer({ diagram }) {
  if (!diagram) return null;
  switch (diagram.type) {
    case "illustration":
      return (
        <Illustration
          src={diagram.src}
          alt={diagram.alt}
          caption={diagram.caption}
          credit={diagram.credit}
        />
      );
    case "cards":
      return <Cards items={diagram.items} />;
    case "flow":
      return <Flow items={diagram.items} />;
    case "balance":
      return <Balance left={diagram.left} right={diagram.right} />;
    case "symptom-grid":
      return <SymptomGrid items={diagram.items} />;
    case "pathway":
      return <Pathway steps={diagram.steps} />;
    case "definition-box":
      return <DefinitionBox {...diagram} />;
    case "enzyme-diagram":
      return <EnzymeDiagram steps={diagram.steps} />;
    case "two-column":
      return <TwoColumn left={diagram.left} right={diagram.right} />;
    case "cause-web":
      return <CauseWeb items={diagram.items} />;
    case "food-grid":
      return <FoodGrid categories={diagram.categories} />;
    case "probiotic-table":
      return <ProbioticTable items={diagram.items} />;
    case "supplement-list":
      return <SupplementList items={diagram.items} />;
    default:
      return null;
  }
}
